import {
    addMonths,
    endOfMonth,
    format,
    parseISO,
    startOfMonth,
} from "date-fns";
import { DescriptionListItem } from "@design-system/DescriptionList/DescriptionList";
import taskStyles from "@components/drawers/TaskDrawerFields.module.scss";
import { FINANCIAL_ACCOUNT_TYPE_LABEL } from "@common/constants";
import {
    FINANCIAL_TYPES_AVAILABLE_FOR_STATEMENT_RETRIEVAL,
    isStatementRetrievalFinancialAccountType,
} from "@common/helpers/financialAccount";
import {
    FinancialAccount,
    FinancialAccountType,
} from "@common/types/financialAccount";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { useScope } from "@state/scopeContext.lib";
import { FinancialAccountCombobox } from "@components/financialAccount/AccountsFilter/FinancialAccountCombobox";
import {
    parseDocumentRetrievalCloseTemplateViewConfig,
    readDocumentRetrievalFinancialAccountType,
    readDocumentRetrievalFinancialAccountIds,
    readDocumentRetrievalStartDateOverride,
    readDocumentRetrievalEndDateOverride,
    serializeDocumentRetrievalCloseTemplateViewConfigWithDates,
} from "./closeTemplateTaskViewConfig";

interface Props {
    htmlIdPrefix: string;
    viewConfig: Record<string, unknown> | null;
    onViewConfigChange: React.Dispatch<
        React.SetStateAction<Record<string, unknown> | null>
    >;
}

const isUploadStatementsAccount = (account: FinancialAccount): boolean =>
    isStatementRetrievalFinancialAccountType(account.financialType);

const NO_ACCOUNT_TYPE_VALUE = "__none__";
const NO_DATE_VALUE = "__none__";
const MONTHS_BACK = 24;
const MONTHS_FORWARD = 12;

interface MonthOption {
    value: string; // "YYYY-MM"
    label: string; // "Jun 2026"
}

function generateMonthOptions(): MonthOption[] {
    const today = startOfMonth(new Date());
    const options: MonthOption[] = [];
    for (let i = MONTHS_FORWARD; i >= -MONTHS_BACK; i--) {
        const date = addMonths(today, i);
        options.push({
            value: format(date, "yyyy-MM"),
            label: format(date, "MMM yyyy"),
        });
    }
    return options;
}

const MONTH_OPTIONS = generateMonthOptions();

const ACCOUNT_TYPE_ITEMS: Array<PickerItemData<string>> = [
    { value: NO_ACCOUNT_TYPE_VALUE, label: "Any account type" },
    ...FINANCIAL_TYPES_AVAILABLE_FOR_STATEMENT_RETRIEVAL.map((type) => ({
        value: type,
        label: FINANCIAL_ACCOUNT_TYPE_LABEL[type],
    })),
];

function accountTypeItemsForCurrentValue(
    currentAccountType: FinancialAccountType | null,
): Array<PickerItemData<string>> {
    if (
        currentAccountType == null ||
        isStatementRetrievalFinancialAccountType(currentAccountType)
    ) {
        return ACCOUNT_TYPE_ITEMS;
    }
    return [
        ...ACCOUNT_TYPE_ITEMS,
        {
            value: currentAccountType,
            label: FINANCIAL_ACCOUNT_TYPE_LABEL[currentAccountType],
        },
    ];
}

function accountTypeFromPickerValue(
    value: string | null,
    currentAccountType: FinancialAccountType | null,
): FinancialAccountType | null {
    if (value != null && isStatementRetrievalFinancialAccountType(value)) {
        return value;
    }
    if (
        value != null &&
        currentAccountType != null &&
        value === currentAccountType
    ) {
        return currentAccountType;
    }
    return null;
}
const SERVICE_PERIOD_START_LABEL = "Service period start";
const SERVICE_PERIOD_END_LABEL = "Service period end";
const START_MONTH_ITEMS: Array<PickerItemData<string>> = [
    { value: NO_DATE_VALUE, label: SERVICE_PERIOD_START_LABEL },
    ...MONTH_OPTIONS,
];
const END_MONTH_ITEMS: Array<PickerItemData<string>> = [
    { value: NO_DATE_VALUE, label: SERVICE_PERIOD_END_LABEL },
    ...MONTH_OPTIONS,
];

/** A stored month outside the offered range reads as the placeholder, not as a raw value. */
const monthLabel = (monthValue: string, placeholder: string) =>
    MONTH_OPTIONS.find((option) => option.value === monthValue)?.label ??
    placeholder;

function isoToMonthValue(iso: string | null): string {
    if (!iso) {
        return NO_DATE_VALUE;
    }
    try {
        return format(parseISO(iso), "yyyy-MM");
    } catch {
        return NO_DATE_VALUE;
    }
}

function monthValueToStartISO(monthValue: string): string {
    return format(startOfMonth(parseISO(`${monthValue}-01`)), "yyyy-MM-dd");
}

function monthValueToEndISO(monthValue: string): string {
    return format(endOfMonth(parseISO(`${monthValue}-01`)), "yyyy-MM-dd");
}

export const DocumentRetrievalFields = ({
    htmlIdPrefix,
    viewConfig,
    onViewConfigChange,
}: Props) => {
    // A workspaceId in scope means this template task belongs to a
    // client-specific (workspace-scoped) close template — only those can pin
    // exact accounts, since account IDs are workspace-specific. Org-wide
    // templates keep the generic "any account of this type" behavior.
    const { workspaceId } = useScope();
    const isClientSpecific = Boolean(workspaceId);

    const currentAccountType =
        readDocumentRetrievalFinancialAccountType(viewConfig);
    const currentAccountIds =
        readDocumentRetrievalFinancialAccountIds(viewConfig);
    // Pinned accounts win over an account-type filter on serialize, so the
    // type Select must be locked whenever accounts are pinned — regardless of
    // `isClientSpecific` — otherwise a type choice would be silently dropped.
    const hasPinnedAccounts = currentAccountIds.length > 0;
    const currentStartDate = readDocumentRetrievalStartDateOverride(viewConfig);
    const currentEndDate = readDocumentRetrievalEndDateOverride(viewConfig);

    const accountTypePlaceholder = hasPinnedAccounts
        ? "Using specific accounts above"
        : "Any account type";
    const currentStartMonthValue = isoToMonthValue(currentStartDate);
    const currentEndMonthValue = isoToMonthValue(currentEndDate);

    const updateField = (
        overrides: Partial<{
            financialAccountType: FinancialAccountType | null;
            financialAccountIds: number[];
            startDateOverride: string | null;
            endDateOverride: string | null;
        }>,
    ) => {
        onViewConfigChange((prev) => {
            const base = parseDocumentRetrievalCloseTemplateViewConfig(prev);
            return serializeDocumentRetrievalCloseTemplateViewConfigWithDates(
                base,
                {
                    financialAccountType:
                        overrides.financialAccountType !== undefined
                            ? overrides.financialAccountType
                            : readDocumentRetrievalFinancialAccountType(prev),
                    financialAccountIds:
                        overrides.financialAccountIds ??
                        readDocumentRetrievalFinancialAccountIds(prev),
                    startDateOverride:
                        overrides.startDateOverride !== undefined
                            ? overrides.startDateOverride
                            : readDocumentRetrievalStartDateOverride(prev),
                    endDateOverride:
                        overrides.endDateOverride !== undefined
                            ? overrides.endDateOverride
                            : readDocumentRetrievalEndDateOverride(prev),
                },
            );
        });
    };

    const handleStartMonthChange = (value: string | null) => {
        const isClearing = !value || value === NO_DATE_VALUE;
        if (isClearing) {
            updateField({ startDateOverride: null });
            return;
        }
        const startISO = monthValueToStartISO(value);
        const endISO = monthValueToEndISO(value);
        const endHasValue =
            currentEndMonthValue && currentEndMonthValue !== NO_DATE_VALUE;
        updateField({
            startDateOverride: startISO,
            ...(endHasValue ? {} : { endDateOverride: endISO }),
        });
    };

    const handleEndMonthChange = (value: string | null) => {
        const isClearing = !value || value === NO_DATE_VALUE;
        updateField({
            endDateOverride: isClearing ? null : monthValueToEndISO(value),
        });
    };

    return (
        <>
            {isClientSpecific && (
                <DescriptionListItem label="Financial accounts" alignTop>
                    <div className={taskStyles.detailSelectWrapper}>
                        <FinancialAccountCombobox
                            multiple
                            value={currentAccountIds}
                            onValueChange={(nextAccountIds) => {
                                updateField({
                                    financialAccountIds: nextAccountIds,
                                    // Selecting specific accounts overrides
                                    // the account-type filter — keep only one
                                    // targeting mode active at a time.
                                    ...(nextAccountIds.length > 0
                                        ? { financialAccountType: null }
                                        : {}),
                                });
                            }}
                            buttonSize="md"
                            accountFilterPredicate={isUploadStatementsAccount}
                            hideArrow={false}
                            buttonProps={{
                                className: taskStyles.detailSelectTrigger,
                                variant: "primary",
                                block: false,
                            }}
                        />
                    </div>
                </DescriptionListItem>
            )}
            <DescriptionListItem
                label="Financial account type"
                htmlFor={`${htmlIdPrefix}-financialAccountType`}
            >
                <div className={taskStyles.detailSelectWrapper}>
                    <Picker.Select
                        id={`${htmlIdPrefix}-financialAccountType`}
                        items={accountTypeItemsForCurrentValue(
                            currentAccountType,
                        )}
                        value={currentAccountType ?? NO_ACCOUNT_TYPE_VALUE}
                        renderValue={() =>
                            currentAccountType
                                ? FINANCIAL_ACCOUNT_TYPE_LABEL[
                                      currentAccountType
                                  ]
                                : accountTypePlaceholder
                        }
                        trigger={
                            <Picker.Button
                                variant="primary"
                                className={taskStyles.detailSelectTrigger}
                            />
                        }
                        onValueChange={(value) => {
                            const accountType = accountTypeFromPickerValue(
                                value,
                                currentAccountType,
                            );
                            updateField({
                                financialAccountType: accountType,
                                // Choosing a type clears any pinned accounts —
                                // same mutual-exclusivity as above.
                                ...(accountType != null && hasPinnedAccounts
                                    ? { financialAccountIds: [] }
                                    : {}),
                            });
                        }}
                        disabled={hasPinnedAccounts}
                    />
                </div>
            </DescriptionListItem>
            <DescriptionListItem
                label="Start date"
                htmlFor={`${htmlIdPrefix}-startDateOverride`}
            >
                <div className={taskStyles.detailSelectWrapper}>
                    <Picker.Select
                        id={`${htmlIdPrefix}-startDateOverride`}
                        items={START_MONTH_ITEMS}
                        value={currentStartMonthValue}
                        onValueChange={handleStartMonthChange}
                        renderValue={([month]) =>
                            monthLabel(month, SERVICE_PERIOD_START_LABEL)
                        }
                        trigger={
                            <Picker.Button
                                variant="primary"
                                className={taskStyles.detailSelectTrigger}
                            />
                        }
                    />
                </div>
            </DescriptionListItem>
            <DescriptionListItem
                label="End date"
                htmlFor={`${htmlIdPrefix}-endDateOverride`}
            >
                <div className={taskStyles.detailSelectWrapper}>
                    <Picker.Select
                        id={`${htmlIdPrefix}-endDateOverride`}
                        items={END_MONTH_ITEMS}
                        value={currentEndMonthValue}
                        onValueChange={handleEndMonthChange}
                        renderValue={([month]) =>
                            monthLabel(month, SERVICE_PERIOD_END_LABEL)
                        }
                        trigger={
                            <Picker.Button
                                variant="primary"
                                className={taskStyles.detailSelectTrigger}
                            />
                        }
                    />
                </div>
            </DescriptionListItem>
        </>
    );
};
